import React, { useCallback, useContext, useState } from 'react';
import { useSnackbar } from 'notistack';
import action from '../../components/UI/buttons/NotificationDismissButton';
import { FormControlerContext } from '../form-controler-context/form-controler-context';
import { fileUploadHandler, getDateAndShift } from './helpers';
import { formName, fieldConfig } from './gt-form-config';
import { axiosGu } from 'config';

export const GTFormContext = React.createContext({});

export function GTFormContextProvider({ children }) {
    const {
        fieldsState: { allValues, allErrors, allTouched, allConfigs },
        controlers: {
            setFieldValue,
            setFieldTouched,
            setFieldError,
            resetForm: generalResetForm,
            removeFormState,
            getFormCofig,
            defaultFieldOptions,
        },
        state: { setValues },
    } = useContext(FormControlerContext);

    const { [formName]: values = {} } = allValues;
    const { [formName]: errors = {} } = allErrors;
    const { [formName]: touched = {} } = allTouched;
    const { [formName]: formConfig = {} } = allConfigs;

    const [submitInProgress, setSubmitInProgress] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const setDateAndShift = useCallback(() => {
        const [dateString, shift] = getDateAndShift();
        setFieldValue(formName, fieldConfig.date.fieldName, dateString);
        setFieldValue(formName, fieldConfig.shift.fieldName, shift);
        setFieldError(formName, fieldConfig.date.fieldName, false);
        setFieldError(formName, fieldConfig.shift.fieldName, false);
    }, [setFieldValue, setFieldError]);

    const resetForm = useCallback(
        function (formName) {
            generalResetForm(formName);
            setDateAndShift();
        },
        [generalResetForm, setDateAndShift]
    );

    const changeHandler = useCallback(
        function (e, selectedValue) {
            const enteredValue = selectedValue || selectedValue === null ? selectedValue : e?.target?.value;
            const { fieldName, error, type } = this;

            if (type === 'file') {
                fileUploadHandler(e, setFieldError, setFieldTouched, formName, fieldName, values.fileUpload, setValues);
                return;
            }

            setFieldValue(formName, fieldName, enteredValue);

            if (fieldName === fieldConfig.division.fieldName) {
                setFieldValue(formName, fieldConfig.desk.fieldName, null);
                setFieldError(formName, fieldConfig.desk.fieldName);
            }

            if (fieldName === fieldConfig.shift.fieldName) setFieldTouched(formName, fieldName);

            enteredValue !== '' && error && setFieldError(formName, fieldName, false);
        },
        [setFieldError, setFieldValue, setFieldTouched, values, setValues]
    );

    const removeFile = useCallback(
        function (fieldName, fileName) {
            setValues((state) => {
                const { [fileName]: toBeDeleted, ...rest } = state[formName][fieldName];
                if (!Object.keys(rest).length) setFieldError(formName, fieldName);
                return {
                    ...state,
                    [formName]: {
                        ...state[formName],
                        [fieldName]: {
                            ...rest,
                        },
                    },
                };
            });
        },
        [setValues, setFieldError]
    );

    const bindedDefaultFieldOptions = defaultFieldOptions.bind({ changeHandler }, formName);

    const submitFormHandler = useCallback(
        async function (e) {
            try {
                e.preventDefault();
                if (submitInProgress) return;
                setSubmitInProgress(true);

                let firstErrorField = null;

                const { values, errors } = this;

                const formIsValid = Object.keys(errors).reduce((isValid, key) => {
                    let setIsValid = true;
                    if (errors[key]) {
                        setFieldTouched(formName, key);
                        !firstErrorField && (firstErrorField = key);
                        setIsValid = false;
                    }

                    return isValid ? setIsValid : false;
                }, true);

                if (!formIsValid) {
                    enqueueSnackbar('Please fill all required fields.', {
                        variant: 'warning',
                        action,
                    });

                    const element = document.querySelector(`[name=${firstErrorField}]`);
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'center',
                    });
                    setSubmitInProgress(false);
                    return;
                }

                const { fileUpload, ...formInputs } = values;

                const formData = new FormData();

                Object.keys(fileUpload).forEach((fileName) => {
                    formData.append(`uploadFiles`, fileUpload[fileName].file, fileUpload[fileName].name);
                });

                formData.append(
                    'greatTelepresenceFormSubmit',
                    new Blob([JSON.stringify(formInputs)], {
                        type: 'application/json',
                    })
                );

                await axiosGu.post(`/great-telepresence/submit-form`, formData);

                resetForm(formName);
                enqueueSnackbar('Data successfully sent!', {
                    variant: 'success',
                    action,
                });
            } catch (err) {
                const resStatus = err?.response?.status;
                if (resStatus !== 401) {
                    enqueueSnackbar(`Sending data failed! ${err.message}`, {
                        variant: 'error',
                        action,
                    });
                }
            }
            setSubmitInProgress(false);
        },
        [setFieldTouched, resetForm, enqueueSnackbar, submitInProgress]
    );

    return (
        <GTFormContext.Provider
            value={{
                fieldControlers: {
                    defaultFieldOptions: bindedDefaultFieldOptions,
                    setFieldValue,
                    setFieldError,
                    fieldConfig,
                },
                formControlers: {
                    submitFormHandler,
                    resetForm,
                    removeFormState,
                    getFormCofig,
                },
                submitInProgress,
                fieldsState: { values, errors, touched },
                formConfig,
                formName,
                setDateAndShift,
                removeFile,
            }}
        >
            {children}
        </GTFormContext.Provider>
    );
}
